import React from 'react'
import { useParams } from 'react-router-dom'
import { Layouts } from 'react-grid-layout'
import { v4 as uuidv4 } from 'uuid'
import { styled, Box, Toolbar } from '@mui/material'
import { useSearchParams } from '@edge/spark/utils'
import {
    useAllDashboardsQuery,
    useIsEditingLayouts,
    useTempSaveLayoutsMutation,
    useUpdateUserDashboardsMutation,
    DashboardCardDef,
    useActiveDashboardId,
    useDashboard,
} from '@edge/spark/io-dashboards'
import { useAdminUserAttributes } from '@edge/spark/io-roles'
import { generateLayoutsForGrid, type DashboardSize } from '../Dashboard/DashboardLayout'
import { DashboardHeading } from '../DashboardHeading'
import { DashboardCrud } from '../DashboardCrud'
import { Dashboard } from '../Dashboard/Dashboard'
import { useIsMobile } from '@edge/spark/ui-mui'

const ToolContainer = styled(Toolbar)`
    justify-content: space-between;
    display: flex;
    flex-wrap: wrap-reverse;
`

const ToolWidgetContainer = styled(Box)`
    display: flex;
    align-items: center;
`

export const Homepage: React.FC = () => {
    const { uid: userId } = useSearchParams()
    const { dashId } = useParams<{ dashId?: string }>()

    const adminUserAttributes = useAdminUserAttributes({ userId })
    const activeDashboardId = useActiveDashboardId()
    const [tempSaveLayouts] = useTempSaveLayoutsMutation()
    const [saveUserDashboards] = useUpdateUserDashboardsMutation()
    const mobile = useIsMobile()
    const [dashboardKey, reloadDashboard] = React.useState(Math.random())
    const [dashboardSize, saveDashboardSize] = React.useState<DashboardSize>({
        breakpoint: 'lg',
        cols: 6,
    })

    const isEditingLayouts = useIsEditingLayouts()
    const savedDashboards = useAllDashboardsQuery()
    const activeDashboard = useDashboard({ dashId, userId })
    // savedDashboards.find((dash) => dash.id === (dashId || activeDashboardId)) || savedDashboards[0]

    const saveLayouts = (layouts: Layouts) =>
        tempSaveLayouts({
            variables: { layouts: JSON.stringify(layouts) },
        })

    const cancelLayouts = async () => {
        if (!activeDashboard) return
        const priorLayouts = generateLayoutsForGrid(activeDashboard.cards, dashboardSize.cols)
        await tempSaveLayouts({
            variables: { layouts: JSON.stringify(priorLayouts) },
        })

        reloadDashboard(Math.random())
    }

    const saveCardConfig = async (cardDef: DashboardCardDef) => {
        if (!activeDashboard) return
        const id = uuidv4()

        const cards = activeDashboard.cards.map((c) => (c.id === cardDef.id ? cardDef : c))

        const { name } = activeDashboard

        const dashboards = savedDashboards
            .map((d) => (d.id === activeDashboardId ? { ...d, id, cards, name } : d))
            .map((d) => JSON.stringify(d))

        await saveUserDashboards({
            variables: { dashboards },
            refetchQueries: ['userDashboards'],
        })

        // await updateUser(client)
    }

    const deleteCardFromDashboard = async (cardId: string) => {
        if (!activeDashboard) return
        const id = uuidv4()
        const index = activeDashboard.cards.findIndex((card) => card.id === cardId)

        activeDashboard.cards.splice(index, 1)
        const { cards, name } = activeDashboard
        const dashboards = savedDashboards
            .map((d) => (d.id === activeDashboardId ? { ...d, id, cards, name } : d))
            .map((d) => JSON.stringify(d))

        await saveUserDashboards({
            variables: { dashboards },
            refetchQueries: ['userDashboards'],
        })

        // await updateUser(client)
    }

    if (!activeDashboard) {
        return <div>Loading dashboard...</div>
    }

    return (
        <div>
            <ToolContainer
                sx={
                    isEditingLayouts && mobile
                        ? {
                              justifyContent: 'space-around',
                              marginBottom: '8px',
                              padding: '0px',
                          }
                        : undefined
                }
            >
                <DashboardHeading user={adminUserAttributes} name={activeDashboard.name} />
                <ToolWidgetContainer
                    sx={
                        isEditingLayouts && mobile
                            ? {
                                  width: '100%',
                                  marginBottom: '8px',
                              }
                            : undefined
                    }
                >
                    <DashboardCrud
                        cancelEdits={cancelLayouts}
                        dashboards={savedDashboards}
                        activeDashboard={activeDashboard}
                    />
                </ToolWidgetContainer>
            </ToolContainer>
            <Dashboard
                key={dashboardKey}
                dashboardLayout={activeDashboard}
                saveLayouts={saveLayouts}
                saveCardConfig={saveCardConfig}
                saveDashboardSize={saveDashboardSize}
                deleteCardFromDashboard={deleteCardFromDashboard}
                readonly={isEditingLayouts}
            />
        </div>
    )
}
